import { Box, Button, Flex, IconButton, SentenceDiff, Text } from '@rocket/ui';
import { useState } from 'react';
import { LuBookCopy, LuChevronLeft, LuChevronRight, LuCopy } from 'react-icons/lu';

export interface SampleEdit {
  before: string;
  after: string;
}

export interface RuleSearchResult {
  rule_id: string;
  name: string;
  description?: string;
  train_types?: Array<string>;
  training_count?: number;
  playbook_id?: string;
  playbook_name?: string;
  confidence?: number;
  sample_sentences?: Array<string>;
  sample_edits?: Array<SampleEdit>;
}

interface RuleSearchCardProps {
  rule: RuleSearchResult;
  onSelect: (rule: RuleSearchResult) => void;
  disabled?: boolean;
}

export function RuleSearchCard({ rule, onSelect, disabled }: RuleSearchCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const sampleEdits = rule.sample_edits ?? [];
  const currentSample = sampleEdits[currentIndex];
  const hasSamples = sampleEdits.length > 0 && !!currentSample;

  return (
    <Box borderWidth="1px" borderRadius="md" p="3" bg="bg.surface" mb="2">
      <Flex.V gap="1.5">
        <Flex.H align="center" gap="2">
          <LuBookCopy size={13} color="var(--colors-blue-fg)" />
          <Text fontSize="sm" fontWeight="600" flex="1" lineClamp={1}>
            {rule.name}
          </Text>
        </Flex.H>

        <Text fontSize="xs" color="fg.muted">
          from <strong>{rule.playbook_name ?? 'Unknown playbook'}</strong>
          {rule.training_count != null &&
            rule.training_count > 0 &&
            ` \u2022 ${rule.training_count} examples`}
          {rule.train_types &&
            rule.train_types.length > 0 &&
            ` \u2022 ${rule.train_types.join(', ')}`}
          {rule.confidence != null &&
            rule.confidence > 0 &&
            ` \u2022 ${Math.round(rule.confidence * 100)}% match`}
        </Text>

        {rule.description && (
          <Text fontSize="xs" color="fg.muted" lineClamp={2}>
            {rule.description}
          </Text>
        )}

        {hasSamples && (
          <Box>
            {sampleEdits.length > 1 && (
              <Flex.H justify="space-between" align="center" mb="1">
                <Text fontSize="xs" color="fg.muted" fontWeight="500">
                  Sample Edit
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {currentIndex + 1} of {sampleEdits.length}
                </Text>
              </Flex.H>
            )}

            <SentenceDiff before={currentSample.before} after={currentSample.after} />

            {sampleEdits.length > 1 && (
              <Flex.H justify="center" gap="2" mt="2">
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={() => setCurrentIndex((i) => i - 1)}
                  disabled={currentIndex === 0}
                  aria-label="Previous example"
                >
                  <LuChevronLeft size={12} />
                </IconButton>
                <Flex.H gap="1" align="center">
                  {sampleEdits.map((_, idx) => (
                    <Box
                      key={idx}
                      w="1.5"
                      h="1.5"
                      borderRadius="full"
                      bg={idx === currentIndex ? 'blue.solid' : 'gray.muted'}
                      cursor="pointer"
                      onClick={() => setCurrentIndex(idx)}
                    />
                  ))}
                </Flex.H>
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={() => setCurrentIndex((i) => i + 1)}
                  disabled={currentIndex === sampleEdits.length - 1}
                  aria-label="Next example"
                >
                  <LuChevronRight size={12} />
                </IconButton>
              </Flex.H>
            )}
          </Box>
        )}

        <Button
          size="xs"
          colorPalette="blue"
          variant="outline"
          disabled={disabled}
          onClick={() => onSelect(rule)}
          css={{ alignSelf: 'flex-start', mt: '1' }}
        >
          <LuCopy size={12} />
          Copy this rule
        </Button>
      </Flex.V>
    </Box>
  );
}

interface RuleSearchResultsProps {
  results: Array<RuleSearchResult>;
  onSelect: (rule: RuleSearchResult) => void;
  disabled?: boolean;
}

export function RuleSearchResults({ results, onSelect, disabled }: RuleSearchResultsProps) {
  if (results.length === 0) {
    return null;
  }

  return (
    <Box mt="2" mb="1">
      <Flex.H align="center" gap="2" mb="2">
        <LuBookCopy size={14} color="var(--colors-blue-fg)" />
        <Text fontSize="xs" fontWeight="600">
          {results.length} matching {results.length === 1 ? 'rule' : 'rules'} found
        </Text>
      </Flex.H>
      {results.map((rule) => (
        <RuleSearchCard key={rule.rule_id} rule={rule} onSelect={onSelect} disabled={disabled} />
      ))}
    </Box>
  );
}
